/**
 * The AI's questions about the open document, in the AI panel (R1 Q2; draft
 * page redesign): one pointer row — "8 questions for your team · 2 answered"
 * and "Answer on page". The questions themselves are answered in one place
 * only, the document overview's "Questions for your team"; the panel no longer
 * repeats them.
 *
 * The list is loaded from the server whenever the panel opens on a document;
 * questions raised in a live turn arrive through the agent store's
 * `question_raised` handling, so the count keeps up. Shown while a question
 * waits for an answer.
 *
 * "Answer on page" brings the overview's list into view with its first
 * waiting question open; from any other page it opens the overview there
 * (`#document-questions`).
 */

import { useCallback, useEffect, useMemo } from 'react'
import { useInRouterContext, useNavigate } from 'react-router-dom'
import { ChevronRight, MessageSquare } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DOCUMENT_QUESTIONS_ANCHOR, questionProgress } from '@/lib/question-view'
import {
  PANEL_QUESTIONS_ANSWERED,
  PANEL_QUESTIONS_COUNT_MANY,
  PANEL_QUESTIONS_COUNT_ONE,
  PANEL_QUESTIONS_LINK,
  fillCopy,
} from '@/lib/question-copy'
import { selectDocumentQuestions, useQuestionsStore } from '@/stores/questions-store'

interface PanelQuestionsProps {
  bidId: string
  artifactId: string
  className?: string
}

export function PanelQuestions(props: PanelQuestionsProps) {
  const inRouter = useInRouterContext()
  return inRouter ? <RoutedPanelQuestions {...props} /> : <PanelQuestionsPointer {...props} onNavigate={assignLocation} />
}

function assignLocation(href: string): void {
  window.location.assign(href)
}

function RoutedPanelQuestions(props: PanelQuestionsProps) {
  const navigate = useNavigate()
  const handleNavigate = useCallback((href: string) => void navigate(href), [navigate])
  return <PanelQuestionsPointer {...props} onNavigate={handleNavigate} />
}

function PanelQuestionsPointer({
  bidId,
  artifactId,
  className,
  onNavigate,
}: PanelQuestionsProps & { onNavigate: (href: string) => void }) {
  const load = useQuestionsStore((s) => s.load)
  const requestFocus = useQuestionsStore((s) => s.requestFocus)
  const asks = useQuestionsStore((s) => selectDocumentQuestions(s, artifactId).asks)

  useEffect(() => {
    if (bidId && artifactId) void load(bidId, artifactId)
  }, [bidId, artifactId, load])

  const { answered, total, waiting } = useMemo(() => questionProgress(asks), [asks])
  if (waiting === 0) return null

  const handleOpen = () => {
    // The overview's list is on screen: open it in place. Otherwise go there.
    if (document.getElementById(DOCUMENT_QUESTIONS_ANCHOR)) {
      requestFocus(artifactId)
      return
    }
    onNavigate(`/app/bids/${bidId}/responses/${artifactId}#${DOCUMENT_QUESTIONS_ANCHOR}`)
  }

  const countLine = total === 1 ? PANEL_QUESTIONS_COUNT_ONE : fillCopy(PANEL_QUESTIONS_COUNT_MANY, { count: total })

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-2.5 py-2 text-sm', className)}
      data-testid="panel-questions"
    >
      <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1">
        {countLine}
        <span className="text-meta">{` · ${fillCopy(PANEL_QUESTIONS_ANSWERED, { answered })}`}</span>
      </span>
      <button
        type="button"
        className="inline-flex shrink-0 items-center gap-0.5 text-[hsl(var(--link))] hover:underline"
        onClick={handleOpen}
      >
        {PANEL_QUESTIONS_LINK}
        <ChevronRight className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  )
}
